import { useEffect, useState } from 'react';
import { Select, Typography } from 'antd';
import { useFetcher } from '@remix-run/react';
import { availableLanguages, type AvailableLanguage } from '~/services/i18n';
import { getLanguageDisplayName } from '~/services/languageData';
import { useApplicationContext } from '~/components/ApplicationContext';
import { useLanguage } from '~/contexts/LanguageContext';
import { t } from '~/services/i18n';

const { Text } = Typography;

export default function LangSwitcher() {
    // Get current language from context
    const currentLang = useLanguage();
    const [selectedLang, setSelectedLang] =
        useState<AvailableLanguage>(currentLang);
    const fetcher = useFetcher();
    const appContext = useApplicationContext();

    // Check if user is authenticated
    const isAuthenticated = !!appContext?.account;

    // Sync selected language with context language
    useEffect(() => {
        setSelectedLang(currentLang);
    }, [currentLang]);

    // Reload page when language change is successful for authenticated users
    useEffect(() => {
        if (
            fetcher.state === 'idle' &&
            fetcher.data &&
            typeof fetcher.data === 'object' &&
            'success' in fetcher.data &&
            fetcher.data.success
        ) {
            // For authenticated users, reload the page to get fresh data with new language
            window.location.reload();
        }
    }, [fetcher.state, fetcher.data]);

    const handleLanguageChange = (value: string) => {
        setSelectedLang(value as AvailableLanguage);

        if (isAuthenticated) {
            // Authenticated users: use dedicated API endpoint
            fetcher.submit(
                { language: value },
                { method: 'POST', action: '/user/lang' }
            );
        } else {
            // Non-authenticated users: update URL param
            const url = new URL(window.location.href);
            url.searchParams.set('lang', value);
            // Ensure HTTPS protocol for security
            url.protocol = 'https:';
            window.location.href = url.toString();
        }
    };

    const options = availableLanguages.map((lang) => ({
        value: lang,
        label: getLanguageDisplayName(lang),
    }));

    return (
        <div style={{ marginTop: '12px' }}>
            <Text
                type='secondary'
                style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                }}>
                {t('aiPrompt.language', 'Language')}
            </Text>
            <Select
                value={selectedLang}
                onChange={handleLanguageChange}
                options={options}
                style={{ width: '100%' }}
                size='middle'
                loading={isAuthenticated && fetcher.state !== 'idle'}
                disabled={isAuthenticated && fetcher.state !== 'idle'}
            />
        </div>
    );
}
